import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { IconDeliveryShippingBoxTransit } from "@/assets/icons/figma/IconDeliveryShippingBoxTransit";
import { IconNavigationAddPlus } from "@/assets/icons/figma/IconNavigationAddPlus";
import { IconNavigationCross } from "@/assets/icons/figma/IconNavigationCross";
import { cn } from "@/common/utils/css.utils";
import { useSearchContext } from "@/features/search/context/SearchContext";
import {
  US_ALL_ITEMS_URL_PARAM,
  US_ALL_ITEMS_URL_VALUE,
  isAllItemsInUrl,
} from "@/features/us-market/us-shippable";
import { isUsLocale } from "@/i18n/config";

/**
 * WHO-6510. The "Ships to the US" chip above the results on `/en-us`. On by default (the list only
 * holds what ships to the US, see `withUsShippableDefault`): a filled chip whose cross shows every
 * listing. Off: an outlined chip whose plus puts the filter back. The state is the URL
 * (`allItems=1`), like every other filter, so the server render, a shared link and the back
 * button all agree.
 *
 * Not remembered across pages, unlike the Pro Buyer strip: the next list starts from what a US
 * buyer can actually buy.
 */
export const UsShippableChip = () => {
  const { locale } = useRouter();

  if (!isUsLocale(locale)) {
    return null;
  }

  return <UsShippableChipContent />;
};

const UsShippableChipContent = () => {
  const { t } = useTranslation();
  const { query: routerQuery } = useRouter();
  const {
    query,
    navigation: { setParam },
  } = useSearchContext();
  const filterOn = !isAllItemsInUrl(routerQuery);

  return (
    <div className="flex">
      <button
        type="button"
        aria-pressed={filterOn}
        disabled={query.isPlaceholderData}
        title={
          filterOn
            ? t("listing:us-ships-chip-show-all", {
                defaultValue:
                  "Show all items, also those that don't ship to the US",
              })
            : t("listing:us-ships-chip-show-only", {
                defaultValue: "Show only items that ship to the US",
              })
        }
        onClick={() =>
          setParam(
            US_ALL_ITEMS_URL_PARAM,
            filterOn ? US_ALL_ITEMS_URL_VALUE : null
          )
        }
        className={cn(
          "flex items-center space-x-1.5 rounded-full border py-1 pl-2.5 pr-2 font-body text-sm font-semibold transition-opacity disabled:opacity-60",
          filterOn
            ? "border-brand-500 bg-brand-500 text-white hover:bg-brand-600"
            : "border-brand-500 bg-white text-brand-500 hover:bg-brand-50"
        )}
        data-testid="us-shippable-chip"
      >
        <IconDeliveryShippingBoxTransit className="size-4 shrink-0" />
        <span>
          {t("listing:us-ships-chip", { defaultValue: "Ships to the US" })}
        </span>
        {filterOn ? (
          <IconNavigationCross className="size-4 shrink-0" />
        ) : (
          <IconNavigationAddPlus className="size-4 shrink-0" />
        )}
      </button>
    </div>
  );
};
